import React from 'react';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Table, Button, Container, Form } from 'react-bootstrap';
import Moment from 'react-moment';
import { observer, inject } from 'mobx-react';
import { Client } from '../../models';
import { ClientEntryStore } from '../../stores/client-entry-store';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { LAB_INTEGRATION_VIRTUAL_CLINIC, UserProfile } from '../../models/UserProfile';
import { GenericModal } from '../common/generic-modal';
import { MathUtils } from '../../utils/MathUtils';
import { copyToClipboard } from '../../utils/clipboard';
import { visibleExceptProd } from '../../utils/featureVisibility';

type SortDirection = 'asc' | 'desc';

// Sort by the underlying value (dates/numbers), not the formatted display string.
// Missing values sort to the start in asc / end in desc.
const SORT_ACCESSORS: { [field: string]: (c: Client) => string | number } = {
  lastName: (c) => (c.lastName || '').toLowerCase(),
  firstName: (c) => (c.firstName || '').toLowerCase(),
  gender: (c) => c.displayGender || '',
  dateOfBirth: (c) => c.dateOfBirth || '',
  age: (c) => c.age ?? Number.NEGATIVE_INFINITY,
  ethnicity: (c) => c.displayEthnicity || '',
  menopause: (c) => c.displayMenopause || '',
  trimester: (c) => c.trimester ?? Number.NEGATIVE_INFINITY,
  elevation: (c) => c.elevation ?? Number.NEGATIVE_INFINITY,
  height: (c) => c.height ?? Number.NEGATIVE_INFINITY,
  weight: (c) => c.weight ?? Number.NEGATIVE_INFINITY,
  bmi: (c) =>
    c.height != null && c.weight != null
      ? MathUtils.bmi(c.weight, c.height)
      : Number.NEGATIVE_INFINITY,
  cigaretteSmoker: (c) => (c.cigaretteSmoker ? 1 : 0),
  alcoholDrinker: (c) => (c.alcoholDrinker ? 1 : 0),
  createDate: (c) => c.createDate || '',
};

/**
 * A practitioner hunting for a client thinks in names, not in which column
 * holds which half of one, so "smith", "john", "john smith" and "smith, john"
 * all have to find the same row.
 */
function clientMatchesSearch(client: Client, search: string): boolean {
  const first = (client.firstName || '').toLowerCase();
  const last = (client.lastName || '').toLowerCase();
  return [first, last, `${first} ${last}`, `${last} ${first}`, `${last}, ${first}`]
    .some((candidate) => candidate.includes(search));
}

type Props = {
  clients: Client[];
  currentUserProfile: UserProfile;
  clientEntryStore: ClientEntryStore;
  bloodworkEntryStore: BloodworkEntryStore;
  history?: any;
};

type State = {
  showUpgradeModal: boolean;
  /** Name search. UI only: it never touches what is saved. */
  search: string;
  /** Client whose id was just copied, for the two-second checkmark. */
  copiedClientId?: string;
  sortField: string;
  sortDirection: SortDirection;
};

@inject('clientEntryStore', 'bloodworkEntryStore')
@observer
export class ClientDisplay extends React.Component<Props, State> {
  state: State = {
    showUpgradeModal: false,
    search: '',
    // Last name A-Z is the order a practitioner scans the list in. A
    // just-added client still lands at the top of the list whatever the sort
    // is: that is the recentlySavedClientId pin in render(), not this.
    sortField: 'lastName',
    sortDirection: 'asc'
  };

  private savedRowRef = React.createRef<HTMLTableRowElement>();
  private lastScrolledSavedId?: string;
  private scrollTimer?: number;
  private copyTimer?: number;

  componentDidUpdate(): void {
    // After a save, scroll the just-saved client's row into view so the
    // practitioner can immediately click its "Add Test Results" button.
    const savedId = this.props.clientEntryStore.recentlySavedClientId;
    if (!savedId) {
      this.lastScrolledSavedId = undefined;
      return;
    }
    if (savedId === this.lastScrolledSavedId) {
      return;
    }
    this.lastScrolledSavedId = savedId;
    // Defer the scroll: saving also collapses the add/edit form and shows a
    // success alert, which shift the table's layout. Scrolling immediately would
    // measure the old position and leave the row off-screen, so wait for the
    // dust to settle first.
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      if (this.savedRowRef.current) {
        this.savedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.scrollTimer);
    window.clearTimeout(this.copyTimer);
  }

  handleSort(field: string): void {
    // The practitioner is taking over ordering — stop pinning the saved client
    this.props.clientEntryStore.clearRecentlySavedClient();
    this.setState((prev) => {
      if (prev.sortField === field) {
        return { ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' };
      }
      // Dates default to newest-first; everything else to A→Z / low→high
      const isDate = field === 'createDate' || field === 'dateOfBirth';
      return { ...prev, sortField: field, sortDirection: isDate ? 'desc' : 'asc' };
    });
  }

  renderSortIndicator(field: string): JSX.Element {
    const active = this.state.sortField === field;
    const arrow = !active ? '↕' : this.state.sortDirection === 'asc' ? '▲' : '▼';
    return (
      <span style={{ marginLeft: 4, fontSize: '0.75em', opacity: active ? 1 : 0.35 }}>{arrow}</span>
    );
  }

  sortableHeader(label: React.ReactNode, field: string): JSX.Element {
    return (
      <th
        style={{ verticalAlign: 'bottom', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => this.handleSort(field)}
        title="Click to sort"
      >
        {label}{this.renderSortIndicator(field)}
      </th>
    );
  }

  /**
   * The client's id, for pasting into the same client's Virtual Clinic account.
   * Sibling of Copy Practitioner ID on the Profile page, which is the other
   * half of the same setup and uses the same helper and the same wording.
   */
  copyClientId(client: Client): void {
    const id = client.id;
    if (!id) {
      return;
    }
    copyToClipboard(id).then((copied: boolean) => {
      if (!copied) {
        // Saying "Copied!" over an empty clipboard is worse than saying
        // nothing: the practitioner finds out at the paste.
        alert(
          'Could not copy the Client ID to the clipboard. Please try again, ' +
          'or email support@labsmarts.com.'
        );
        return;
      }
      this.setState({ copiedClientId: id });
      window.clearTimeout(this.copyTimer);
      this.copyTimer = window.setTimeout(
        () => { this.setState({ copiedClientId: undefined }); },
        2000
      );
    });
  }

  /** Also the icon-only button's accessible name, so it is not just an icon. */
  copyButtonTitle(client: Client): string {
    if (client.id && client.id === this.state.copiedClientId) {
      return 'Copied!';
    }
    if (!client.id) {
      return 'This client has no ID yet';
    }
    return `Copy Client ID: ${client.id}`;
  }

  /**
   * "African American" is the only two-word ethnicity, and on one line it sets
   * the column's width for every other row. Broken across two lines the column
   * is no wider than its own heading, which is what leaves room beside it for
   * the Client ID the Virtual Clinic integration needs.
   *
   * A newline under `pre-line` is a hard break the browser honors wherever the
   * column ends up. A <br/> would look the same but leaves no whitespace in the
   * DOM, so the cell would copy out as "AfricanAmerican".
   */
  renderEthnicity(value: string): React.ReactNode {
    return (
      <span style={{ whiteSpace: 'pre-line' }}>{(value || '').replace(/ /g, '\n')}</span>
    );
  }

  addTestResult(client: Client): void {
    const { clientEntryStore, bloodworkEntryStore, history } = this.props;
    clientEntryStore.clearRecentlySavedClient();
    clientEntryStore.setCurrentClient(client);
    bloodworkEntryStore.resetCurrentBloodwork();
    bloodworkEntryStore.setHideEnterTestResults(false);
    history.push(`/bloodwork/edit/${client.id}`);
  }

  deleteClient(client: Client): void {
    const deleteClient = window.confirm('Deleting a client will also delete all the test results for that client.\n\n' + 
      'Are you sure you want to delete this client and their test results?');
    
    if (deleteClient) {
      const { clientEntryStore, bloodworkEntryStore } = this.props;
      bloodworkEntryStore.loadClientBloodworks(client.id).then(() => {
        return bloodworkEntryStore.deleteClientBloodworks(client.id);        
      }).then(() => {
        if (bloodworkEntryStore.clientBloodworks.length === 0) {
          return Promise.resolve();
        } else {  
          return Promise.reject('Not all of the client bloodworks were deleted. Not deleting client.');
        }
      }).then(() => {
        return clientEntryStore.deleteClient(client);
      }).then(() => {
        return clientEntryStore.resetCurrentClient();
      }).catch((error) => {
        console.error(error);
        alert('There was an error trying to delete the client and their test results.');
      });
    }
  }

  editClient(client: Client) {
    const { currentUserProfile, clientEntryStore, history } = this.props;
    if (currentUserProfile.editClientAllowed) {
      // Clear any prior highlight; the next save re-surfaces the edited client
      clientEntryStore.clearRecentlySavedClient();
      if (history && history.location && history.location.pathname !== '/client/edit/manage') {
        history.push('/client/edit/manage');
      }
      clientEntryStore.setCurrentClient(client);
      clientEntryStore.setExpandAddClient(true);
      // This is a hack to expand add/edit client panel on first click of edit
      setTimeout(()=>{
        clientEntryStore.setExpandAddClient(true);
        this.scrollTop();
      }, 1000);
    } else {
      this.setState({
        showUpgradeModal: true
      });
    }
  }

  scrollTop() {
    document.body.scrollTop = 0; 
    document.documentElement.scrollTop = 0;
  }

  render() {
    const {
      clients,
      clientEntryStore,
      currentUserProfile,
      bloodworkEntryStore,
    } = this.props;
    const { showUpgradeModal } = this.state;

    if (!clients || !clients.length) {
      return null;
    }

    const savedId = clientEntryStore.recentlySavedClientId;
    // Off until the practitioner ticks "Virtual Clinic Integration" on their
    // Account profile, which is the same consent the ingest endpoint checks and
    // reads as off for a profile saved before the field existed. A practitioner
    // who is not integrating never sees a column they have no use for, and the
    // env gate stays on top of it while the integration is still unreleased.
    const showClientId =
      visibleExceptProd() &&
      !!currentUserProfile &&
      currentUserProfile.allowsLabIntegration(LAB_INTEGRATION_VIRTUAL_CLINIC);
    const search = this.state.search.trim().toLowerCase();
    const matching = search
      ? clients.filter((c: Client) => clientMatchesSearch(c, search))
      : clients;
    // Name as the tiebreaker, so two clients sharing a sex or an age do not
    // trade places between renders. Sorting by a name column already leads with
    // that name, and repeating it here costs nothing.
    let sortedClients = _.orderBy(
      matching,
      [
        SORT_ACCESSORS[this.state.sortField] || SORT_ACCESSORS.lastName,
        SORT_ACCESSORS.lastName,
        SORT_ACCESSORS.firstName
      ],
      [this.state.sortDirection, 'asc', 'asc']
    );
    // Surface the just-added/edited client at the top, regardless of sort, so
    // its "Add Test Results" button is immediately reachable.
    if (savedId) {
      const idx = sortedClients.findIndex((c: Client) => c.id === savedId);
      if (idx > 0) {
        sortedClients = [
          sortedClients[idx],
          ...sortedClients.slice(0, idx),
          ...sortedClients.slice(idx + 1)
        ];
      }
    }

    return (
      <>
        <Container fluid>
          <Card>
            <Card.Header>Clients</Card.Header>
            <Card.Body>
              <p style={{ fontSize: '13px', color: '#212529', marginBottom: '10px' }}>
                Click any column heading to sort the list by that column; click
                the same heading again to reverse the order.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '10px'
                }}
              >
                <Form.Control
                  type="text"
                  style={{ fontSize: '13px', height: '31px', maxWidth: '260px' }}
                  placeholder="Search by name"
                  value={this.state.search}
                  onChange={(e: any) => { this.setState({ search: e.target.value }); }}
                />
                {search ? (
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    Showing {sortedClients.length} of {clients.length}
                  </span>
                ) : null}
              </div>
              <div>
                { (sortedClients.length === 0) ?
                  <p style={{ fontSize: '13px', color: '#868e96', marginBottom: 0 }}>
                    No clients match that search.
                  </p> :
                  <Table striped bordered hover className="labsmartz-table">
                    <thead>
                      <tr>
                        <th style={{ verticalAlign: 'bottom', textAlign: 'center' }}>Actions</th>
                        {this.sortableHeader('Last Name', 'lastName')}
                        {this.sortableHeader('First Name', 'firstName')}
                        {this.sortableHeader('Sex', 'gender')}
                        {this.sortableHeader(<>Date<br/>of Birth</>, 'dateOfBirth')}
                        {this.sortableHeader('Age', 'age')}
                        {this.sortableHeader('Ethnicity', 'ethnicity')}
                        {this.sortableHeader(<>Menopause/<br/>Pregnant</>, 'menopause')}
                        {this.sortableHeader('Trimester', 'trimester')}
                        {this.sortableHeader('Elevation', 'elevation')}
                        {this.sortableHeader('Height', 'height')}
                        {this.sortableHeader('Weight', 'weight')}
                        {this.sortableHeader('BMI', 'bmi')}
                        {this.sortableHeader(<>Inhaled<br/>Smoke</>, 'cigaretteSmoker')}
                        {this.sortableHeader(<>Alcohol<br/>Drinker</>, 'alcoholDrinker')}
                        {this.sortableHeader(<>Date<br/>Created</>, 'createDate')}
                        {/* Far right, and not sortable: an opaque uuid has no
                            order worth sorting by, and a practitioner scanning
                            the list is never looking for this column. It is
                            here rather than in the Add/Edit Client form because
                            switching the integration on means linking a whole
                            existing roster, which is work you do down a list.

                            Gated with the Profile page's Practitioner ID block,
                            which is the step before this one: without it there
                            is no account to paste these into, so shipping this
                            alone would be a button that leads nowhere. */}
                        {showClientId ? (
                          <th style={{ verticalAlign: 'bottom', textAlign: 'center' }}>
                            Copy<br/>Client ID
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {
                        sortedClients.map((client: Client, index: number) => {
                          const isRecentlySaved = client.id != null && client.id === savedId;
                          return (
                            <tr
                              key={client.id || index}
                              ref={isRecentlySaved ? this.savedRowRef : undefined}
                              className={isRecentlySaved ? 'client-row-recent' : undefined}
                            >
                              <td style={{ width: '150px' }}>
                                <Button 
                                  type="button"
                                  title="Add Test Results"
                                  onClick={() => { this.addTestResult(client); }}
                                >
                                  <FontAwesomeIcon icon="tint" />
                                </Button>
                                &nbsp;&nbsp;
                                <Button 
                                  type="button"
                                  title="Edit Client"
                                  onClick={() => { this.editClient(client); }}
                                >
                                  <FontAwesomeIcon icon="user-edit" />
                                </Button>
                                &nbsp;&nbsp;
                                <Button
                                  title="Delete Client"
                                  onClick={() => { this.deleteClient(client); }}
                                ><FontAwesomeIcon icon="trash-alt" />
                                </Button>                            
                              </td> 
                              <td>{client.lastName}</td>                          
                              <td>{client.firstName}</td>
                              <td>{client.displayGender}</td>
                              <td><Moment format="MM/DD/YYYY">{client.dateOfBirth}</Moment></td>
                              <td>{client.displayAge}</td>
                              <td>{this.renderEthnicity(client.displayEthnicity)}</td>
                              <td>{client.displayMenopause}</td>
                              <td>{client.displayTrimester}</td>
                              <td>{client.displayElevation}</td>
                              <td>{client.displayHeight}</td>
                              <td>{client.displayWeight}</td>
                              <td>{client.displayBMI}</td>
                              <td>{client.displayInhaledSmoke}</td>
                              <td>{client.displayAlcoholDrinker}</td>
                              <td><Moment format="MM/DD/YYYY">{client.createDate}</Moment></td>
                              {showClientId ? (
                                <td style={{ textAlign: 'center' }}>
                                  <Button
                                    type="button"
                                    variant="outline-primary"
                                    size="sm"
                                    // No label: the heading above already reads
                                    // "Copy Client ID", and repeating it on every
                                    // row is what made this column wide.
                                    title={this.copyButtonTitle(client)}
                                    aria-label={this.copyButtonTitle(client)}
                                    disabled={!client.id}
                                    onClick={() => { this.copyClientId(client); }}
                                  >
                                    {/* fixedWidth so swapping in the checkmark
                                        does not resize the button under the
                                        cursor: the two glyphs differ in width. */}
                                    <FontAwesomeIcon
                                      fixedWidth
                                      icon={client.id === this.state.copiedClientId ? 'check' : 'copy'}
                                    />
                                  </Button>
                                </td>
                              ) : null}
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </Table>
                }
              </div>
            </Card.Body>
          </Card>       
        </Container>
        <GenericModal 
          show={showUpgradeModal} 
          title="Edit Client Feature Not Available"
          text="Edit Client feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => { this.setState( { showUpgradeModal: false }); }}
        />
      </>
    );
  }
}
